import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { CreditCard, CheckCircle2, AlertCircle, ArrowLeft, MessageSquare, Star } from 'lucide-react'
import { app, db } from '../firebase/config'
import { collection, addDoc, Timestamp, doc, getDoc } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import toast from 'react-hot-toast'

const PAYSTACK_PUBLIC_KEY = 'pk_live_d0dac42be7424384d1b9c6dc660d3993efaf5cd6'

const ManualBalancePayment = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  const [amount, setAmount] = useState('')
  const [phone, setPhone] = useState('')
  const [name, setName] = useState('')
  const [processingPayment, setProcessingPayment] = useState(false)
  const [showCompleteButton, setShowCompleteButton] = useState(false)
  const [paymentReference, setPaymentReference] = useState('')
  const [paymentCompleted, setPaymentCompleted] = useState(false)
  const [applyPaystackFeeUplift, setApplyPaystackFeeUplift] = useState(true)

  // Prefill from link (amount + phone)
  useEffect(() => {
    const amountParam = searchParams.get('amount')
    const phoneParam = searchParams.get('phone')

    if (amountParam) {
      const numeric = Number(amountParam)
      if (!Number.isNaN(numeric) && numeric > 0) {
        setAmount(numeric.toString())
      }
    }
    if (phoneParam) {
      setPhone(phoneParam)
    }
  }, [searchParams])

  useEffect(() => {
    const loadFeeFlag = async () => {
      try {
        const settingsDocRef = doc(db, 'settings', 'booking')
        const snap = await getDoc(settingsDocRef)
        if (snap.exists()) {
          const data = snap.data()
          if (typeof data.applyPaystackFeeUplift === 'boolean') {
            setApplyPaystackFeeUplift(data.applyPaystackFeeUplift)
          }
        }
      } catch (e) {
        console.error('Failed to load payment fee settings:', e)
      }
    }
    loadFeeFlag()
  }, [])

  // Handle payment via Paystack inline (client-side)
  const handlePayment = async (e) => {
    e.preventDefault()
    
    const paymentAmount = Number(amount)
    
    if (!name.trim() || !phone.trim() || !amount || paymentAmount <= 0) {
      toast.error('Please fill in all fields with valid information')
      return
    }
    
    if (paymentAmount < 1) {
      toast.error('Payment amount must be at least GHS 1.00')
      return
    }
    
    setProcessingPayment(true)
    
    // Build a safe email from phone
    const digits = phone.replace(/\D/g, '')
    const customerEmail = (digits || 'customer') + '@temp.sparadiseluxe.com'
    const amountToCharge = paymentAmount * (applyPaystackFeeUplift ? 1.02 : 1)
    
    const startPayment = () => {
      if (!window.PaystackPop) {
        toast.error('Payment system not available. Please check your connection and try again.')
        setProcessingPayment(false)
        return
      }
      
      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email: customerEmail,
        amount: Math.round(amountToCharge * 100), // GHS → pesewas
        currency: 'GHS',
        ref: `MANUAL_BAL_${Date.now()}`,
        callback: function (response) {
          // Payment successful: verify and save manual payment
          setProcessingPayment(false)
          const ref = response.reference
          setPaymentReference(ref)
          // Reuse existing verification + completion flow
          handleCompletePayment(ref)
        },
        onClose: function () {
          setProcessingPayment(false)
          toast.error('Payment was cancelled. Please try again.')
        },
      })
      
      handler.openIframe()
    }
    
    try {
      // If script already loaded, start immediately
      if (window.PaystackPop) {
        startPayment()
        return
      }
      
      // Otherwise, load Paystack inline script (v1) then start
      const script = document.createElement('script')
      script.src = 'https://js.paystack.co/v1/inline.js'
      script.onload = startPayment
      script.onerror = () => {
        toast.error('Failed to load payment gateway. Please try again.')
        setProcessingPayment(false)
      }
      document.body.appendChild(script)
    } catch (error) {
      console.error('Error starting payment:', error)
      toast.error('An error occurred. Please try again.')
      setProcessingPayment(false)
    }
  }
  
  // Verify and complete payment
  const handleCompletePayment = async (referenceOverride = null) => {
    const referenceToUse = referenceOverride || paymentReference
    if (!referenceToUse) {
      toast.error('Payment reference not found. Please try again.')
      return
    }
    
    setProcessingPayment(true)
    setShowCompleteButton(false)
    
    try {      
      const functionsInstance = getFunctions(app)
      
      const verificationToast = toast.loading('Verifying payment status...', {
        duration: 5000,
      })
      
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Verify payment
      const verifyPayment = httpsCallable(functionsInstance, 'verifyBookingDepositPayment')
      const verifyResult = await verifyPayment({
        reference: referenceToUse
      })
      
      const verifyData = verifyResult.data
      
      toast.dismiss(verificationToast)
      
      if (!verifyData || !verifyData.success) {
        const paymentStatus = verifyData?.status || 'unknown'
        if (paymentStatus === 'abandoned' || paymentStatus === 'cancelled') {
          toast.error('Payment was cancelled. Please try again.')
          setPaymentReference('')
        } else {
          toast.error('Payment verification failed. Please try again.')
          setShowCompleteButton(true)
        }
        setProcessingPayment(false)
        return
      }
      
      // Save payment to Firestore
      try {
        await addDoc(collection(db, 'manualPayments'), {
          name: name.trim(),
          phone: phone.trim(),
          amount: Number(amount).toFixed(2),
          transactionRef: verifyData.reference,
          date: Timestamp.now(),
          createdAt: Timestamp.now(),
          status: 'completed',
          metadata: {
            type: 'manual_balance_payment',
            customerName: name.trim(),
            customerPhone: phone.trim(),
            amount: Number(amount).toFixed(2)
          }
        })
      } catch (saveError) {
        console.error('Error saving manual payment to Firestore:', saveError)
        // Don't fail the payment if save fails
      }
      
      // Send thank you SMS with review link
      try {
        // Extract and clean phone number - find first valid phone number
        let rawPhone = phone.trim()
        
        // Extract first valid phone number pattern
        // Match: 0 followed by 9 digits, or 233 followed by 9 digits, or +233 followed by 9 digits
        let customerPhone = ''
        
        // Try to find a valid Ghana phone number pattern
        // Pattern 1: 0XXXXXXXXX (10 digits starting with 0)
        const pattern1 = rawPhone.match(/0\d{9}/)
        if (pattern1) {
          customerPhone = '233' + pattern1[0].substring(1)
        } else {
          // Pattern 2: 233XXXXXXXXX (12 digits starting with 233)
          const pattern2 = rawPhone.match(/233\d{9}/)
          if (pattern2) {
            customerPhone = pattern2[0]
          } else {
            // Pattern 3: +233XXXXXXXXX
            const pattern3 = rawPhone.match(/\+233\d{9}/)
            if (pattern3) {
              customerPhone = pattern3[0].substring(1)
            } else {
              // Fallback: extract first sequence of 9-12 digits
              const digits = rawPhone.match(/\d{9,12}/)
              if (digits) {
                customerPhone = digits[0]
                // Format with country code if needed
                if (customerPhone.startsWith('0')) {
                  customerPhone = '233' + customerPhone.substring(1)
                } else if (!customerPhone.startsWith('233')) {
                  customerPhone = '233' + customerPhone
                }
              }
            }
          }
        }
        
        // Validate phone number (should be 12 digits: 233 + 9 digits)
        if (customerPhone.length !== 12 || !customerPhone.startsWith('233')) {
          customerPhone = ''
        }
        
        const customerName = name.trim()
        
        if (customerPhone && customerPhone.length === 12 && customerName) {
          const functionsInstance = getFunctions(app)
          const sendPaymentCompletion = httpsCallable(functionsInstance, 'sendPaymentCompletionSMS')
          
          await sendPaymentCompletion({
            name: customerName,
            phone: customerPhone
          })
        }
      } catch (smsError) {
        // Don't fail the payment if SMS fails
      }
      
      toast.success('Payment completed successfully!')
      setProcessingPayment(false)
      setShowCompleteButton(false)
      setPaymentReference('')
      setPaymentCompleted(true)
      
      // Reset form
      setName('')
      setPhone('')
      setAmount('')
    } catch (error) {
      console.error('Error verifying payment:', error)
      setProcessingPayment(false)
      setShowCompleteButton(true)
      toast.error('Failed to verify payment. Please try again or contact support.')
    }
  }
  
  return (
    <div className="spa-section min-h-screen bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
      <div className="container-spa max-w-2xl mx-auto px-4 py-12">
        <div className="card bg-white rounded-2xl shadow-xl border border-purple-100 p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-6 shadow-lg">
              <CreditCard className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-800 mb-2">
              Balance Payment
            </h1>
            <p className="text-gray-600">
              Enter your payment details to complete the transaction
            </p>
          </div>
          
          {/* Show payment form only if payment not completed */}
          {!paymentCompleted ? (
            <>
              {/* Payment Form */}
              <form onSubmit={handlePayment} className="space-y-6">
                {/* Name Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all"
                    required
                    disabled={processingPayment || showCompleteButton}
                  />
                </div>
                
                {/* Phone Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="024 XXX XXXX"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all"
                    required
                    disabled={processingPayment || showCompleteButton}
                  />
                </div>
                
                {/* Amount Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount to Pay (GHS) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all text-lg font-semibold"
                    required
                    disabled={processingPayment || showCompleteButton}
                  />
                  {amount && Number(amount) > 0 && (
                    <p className="mt-2 text-sm text-gray-600">
                      Total: <span className="font-semibold text-purple-600">
                        GHS {(Number(amount) * (applyPaystackFeeUplift ? 1.02 : 1)).toFixed(2)}
                      </span>
                      {applyPaystackFeeUplift && ' (includes 2% charges fee)'}
                    </p>
                  )}
                </div>
                
                {/* Payment Button */}
                <div className="space-y-4 pt-4">
                  <button
                    type="submit"
                    disabled={processingPayment || !name.trim() || !phone.trim() || !amount || Number(amount) <= 0}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                  >
                    {processingPayment ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5" />
                        <span>Proceed to Payment</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
              
              {/* Info Notice */}
              <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-800">
                  <strong>Note:</strong> After clicking \"Proceed to Payment\", you'll be redirected to PayStack to complete your payment securely.
                </p>
              </div>
              
              {/* Back Button */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Back to Home</span>
                </button>
              </div>
            </>
          ) : (
            /* Payment Success - Review Link */
            <div className="mt-6 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
              <div className="text-center">
                <CheckCircle2 className="w-16 h-16 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Payment Successful!</h3>
                <p className="text-gray-600 mb-6">
                  Thank you for your payment. We hope you enjoyed your service!
                </p>
                <Link
                  to="/?review=true"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <MessageSquare className="w-5 h-5" />
                  <span>Share Your Experience</span>
                  <Star className="w-5 h-5" />
                </Link>
                <div className="mt-4">
                  <button
                    onClick={() => navigate('/')}
                    className="text-gray-600 hover:text-gray-800 text-sm underline"
                  >
                    Go to Home
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ManualBalancePayment

